import React, { ForwardRefExoticComponent, RefAttributes, useContext, useEffect } from "react";
import { Form, Formik, FormikProps } from "formik";
import * as Yup from "yup";
import Grid from "@material-ui/core/Grid/Grid";
import { makeStyles, Theme, createStyles, Box, TextFieldProps } from "@material-ui/core";
import { AxiosResponse } from "axios";
import { useHistory, useParams } from "react-router-dom";
import CustomButton from "../../../components/Button/CustomButton";
import ProductApi from "../../../api/services/ProductApi";
import CustomSearchAutocomplete, {
  CustomSearchAutocompleteProps,
} from "../../../components/Autocomplete/CustomSearchAutoComplete";
import PagedResponse from "../../../api/models/PagedResponse";
import useEnv from "../../../hooks/env";
import RequestsPagePathParams from "../../../api/models/test-request/RequestsPagePathParams";
import UnknownSampleAddFormType from "../../../api/models/product/UnknownSampleAddFormType";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import UnknownProductAlert from "../../Products/UnknownProduct/UnknownProductAlert";

type UnknownProduct = ProductRaw.Schemas.UnknownProductResponse;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    alignRight: {
      textAlign: "right",
    },
    marginTop: {
      marginTop: theme.spacing(2),
    },
  }),
);

const UnknownProductSearchAutocomplete = CustomSearchAutocomplete as ForwardRefExoticComponent<
  MergedProps<CustomSearchAutocompleteProps<UnknownProduct>, TextFieldProps> & RefAttributes<{ reset: () => void }>
>;

interface UnknownSampleAddFormProps {
  halfWidth?: boolean;
  hideSubmit?: boolean;
  handleFormChange?: (values: UnknownSampleAddFormType) => void;
  handleSubmit?: (values: UnknownSampleAddFormType) => void;
}

const UnknownSampleAddForm = (props: UnknownSampleAddFormProps) => {
  const { halfWidth, hideSubmit, handleFormChange, handleSubmit } = props;
  const { api } = useEnv();
  const productApi = new ProductApi(api);
  const history = useHistory();
  const { testRequestId, tabName } = useParams<RequestsPagePathParams>();
  const classes = useStyles();
  const { handleHttpError } = useContext(ErrorSnackbarContext);

  const yupValidationSchema = Yup.object().shape({
    unknownProduct: Yup.mixed().required("Please select"),
  });

  const handleNavigation = () => {
    history.push(`/products/unknown?backReference=${testRequestId}&backReferenceTab=${tabName}`);
  };

  return (
    <>
      <Box marginBottom={3}>
        <UnknownProductAlert />
      </Box>
      <Formik
        initialValues={{
          unknownProduct: null,
        }}
        onSubmit={(values) => {
          if (handleSubmit) {
            handleSubmit(values);
          }
        }}
        validationSchema={yupValidationSchema}
      >
        {(formikProps: FormikProps<UnknownSampleAddFormType>): JSX.Element => {
          const { errors, isSubmitting, touched, values, setFieldValue } = formikProps;

          useEffect(() => {
            if (handleFormChange) {
              handleFormChange(values);
            }
          }, [values]);

          return (
            <Form>
              <Grid container justify="flex-start" direction="row" spacing={4}>
                <Grid item xs={halfWidth ? 6 : 12}>
                  <UnknownProductSearchAutocomplete
                    autoFocus
                    inputLabel="Unknown Product Name"
                    optionFetch={(query: string): Promise<PagedResponse<UnknownProduct>> => {
                      return productApi
                        .getUnknownProducts(query)
                        .then((res: AxiosResponse) => {
                          return res?.data;
                        })
                        .catch(handleHttpError);
                    }}
                    onChange={(e, value): void => setFieldValue("unknownProduct", value)}
                    mandatory
                    map={(value): { key: string; label: string } => ({
                      key: `${value.id}`,
                      label: `${value.name}`,
                    })}
                    value={values.unknownProduct}
                    error={!!(errors.unknownProduct && touched.unknownProduct)}
                  />
                </Grid>
              </Grid>
              <Grid container item direction="row" justify="flex-start">
                <span onClick={handleNavigation} className="helperTextLink" role="link" tabIndex={0}>
                  Not found? Create new Unknown Product
                </span>
              </Grid>
              {!hideSubmit && (
                <Grid container item direction="row" justify="flex-end">
                  <CustomButton
                    className={classes.marginTop}
                    disabled={isSubmitting}
                    primary
                    text="Use"
                    type="submit"
                  />
                </Grid>
              )}
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default UnknownSampleAddForm;
